import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Compute the post-login redirect target from the `next` query param.
   *
   * Purpose and role in the broader system:
   * - Nginx redirects unauthenticated /rag requests to /login?next=/rag/ so the
   *   user can authenticate via the main app. After a successful login, we must
   *   return the user to the originally requested page.
   * - For /rag paths, we force a full page load (window.location.assign) so the
   *   request goes through Nginx and loads the separate RAG SPA rather than a
   *   client-side React route inside the main app.
   * - We also guard against open redirects by only allowing absolute-path
   *   redirects that start with '/'.
   */
  const redirectTarget = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const next = params.get('next');
      if (!next) return '/messages';
      // Only allow same-origin absolute paths
      if (!next.startsWith('/')) return '/messages';
      return next;
    } catch (_) {
      return '/messages';
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await login(username, password);
    if (success) {
      // If redirecting to /rag, perform a full navigation so Nginx serves the RAG app.
      if (redirectTarget.startsWith('/rag')) {
        window.location.assign(redirectTarget);
        return;
      }
      navigate(redirectTarget);
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5">
            Slack Archive Viewer
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 